import {
  IdentityApi,
  OpenIdConnectApi,
  DiscoveryApi,
} from '@backstage/core-plugin-api';

import { Message, Feedback, UserEmailMode } from '../types';
import { createTimestamp } from '../utils';
import axios from 'axios';

export interface IChatbotApiOptions {
  requestTimeout?: number;
  useOpenIDToken?: boolean;
  userEmailMode?: UserEmailMode;
  autoReloadOnTokenExpiry?: boolean;
  feedbackEndpoint?: string | null;
  customCallPrefix?: string;
}

export class ChatbotApi {
  private contextId: string;
  private identityApi: IdentityApi;
  // private openIdConnectApi: OpenIdConnectApi | null;
  private discoveryApi: DiscoveryApi;

  // private useOpenIDToken: boolean;
  private userEmailMode: UserEmailMode;
  // private autoReloadOnTokenExpiry: boolean;
  private feedbackEndpoint: string | null;
  private customCallPrefix: string;

  private externalTokens: Map<string, string> = new Map();

  constructor(
    options: {
      identityApi: IdentityApi;
      discoveryApi: DiscoveryApi;
      openIdConnectApi?: OpenIdConnectApi | null;
    },
    apiOptions?: IChatbotApiOptions,
  ) {
    this.contextId = '';

    this.identityApi = options.identityApi;
    this.discoveryApi = options.discoveryApi;
    // this.openIdConnectApi = options.openIdConnectApi ?? null;

    // this.useOpenIDToken = apiOptions?.useOpenIDToken ?? false;
    // this.autoReloadOnTokenExpiry =
    //   apiOptions?.autoReloadOnTokenExpiry ?? true;
    this.feedbackEndpoint = apiOptions?.feedbackEndpoint ?? null;
    this.userEmailMode = apiOptions?.userEmailMode ?? 'none';
    this.customCallPrefix = apiOptions?.customCallPrefix ?? '';
  }

  // =========================
  // 🔐 TOKEN (SIN CAMBIOS)
  // =========================

  // private decodeTokenExpiration(token: string): number | null {
  //   try {
  //     const parts = token.split('.');
  //     if (parts.length !== 3) return null;

  //     const payload = parts[1];
  //     const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  //     const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

  //     const decoded = JSON.parse(atob(padded));
  //     return decoded.exp ?? null;
  //   } catch {
  //     return null;
  //   }
  // }

  // private isTokenExpired(token: string): boolean {
  //   const exp = this.decodeTokenExpiration(token);
  //   if (!exp) return true;

  //   const now = Math.floor(Date.now() / 1000);
  //   return exp <= now;
  // }

  private async getToken(): Promise<string | undefined> {
    // if (this.useOpenIDToken) {
    //   const token = await this.openIdConnectApi?.getIdToken();

    //   if (token && this.isTokenExpired(token)) {
    //     if (this.autoReloadOnTokenExpiry) {
    //       this.showToastWithCountdown(
    //         '🔄 Authentication token expired. Reloading page in',
    //         5,
    //       );
    //       await new Promise(r => setTimeout(r, 5000));
    //       window.location.reload();
    //     }
    //     throw new Error('Token expired');
    //   }

    //   return token;
    // }

    const credentials = await this.identityApi.getCredentials();
    return credentials.token;
  }

  // =========================
  // 👤 USER EMAIL
  // =========================

  private async getUserEmail(): Promise<string | undefined> {
    try {
      const profile = await this.identityApi.getProfileInfo();
      return profile.email;
    } catch {
      return undefined;
    }
  }

  private async prepareMessageWithEmail(msg: string) {
    let messageText = msg;
    let metadata: Record<string, any> | undefined;

    if (this.userEmailMode !== 'none') {
      const email = await this.getUserEmail();

      if (email) {
        if (this.userEmailMode === 'message') {
          messageText = `The user email is ${email}\n\n${msg}`;
        } else {
          metadata = { user_email: email };
        }
      }
    }

    return { messageText, metadata };
  }

  private prepareMessageWithCustomCall(msg: string): string {
    return this.customCallPrefix
      ? `${this.customCallPrefix} ${msg}`
      : msg;
  }

  // =========================
  // 🌐 BACKEND URL
  // =========================

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('agent-forge');
  }

  private buildHeaders(token?: string): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const headerMap: Record<string, string> = {
      github: 'X-GitHub-Token',
      jira: 'X-Jira-Token',
      confluence: 'X-Confluence-Token',
    };

    for (const [provider, token] of this.externalTokens.entries()) {
      const headerName = headerMap[provider];
      if (headerName) headers[headerName] = token;
    }

    return headers;
  }

  // =========================
  // 🚀 SEND (NO STREAM)
  // =========================

  public async submitA2ATask(
    newContext: boolean,
    msg: string,
    sessionContextId?: string,
  ) {
    const token = await this.getToken();
    const baseUrl = await this.getBaseUrl();

    const processedMsg = this.prepareMessageWithCustomCall(msg);
    const { messageText, metadata } =
      await this.prepareMessageWithEmail(processedMsg);

    const res = await fetch(`${baseUrl}/chat`, {
      method: 'POST',
      headers: this.buildHeaders(token),
      body: JSON.stringify({
        msg: messageText,
        metadata,
        newContext,
        contextId: sessionContextId || this.contextId,
      }),
    });

    const data = await res.json();

    if (data?.contextId) {
      this.contextId = data.contextId;
    }

    return data;
  }

  // =========================
  // 🌊 STREAMING
  // =========================

  public async *submitA2ATaskStream(
    newContext: boolean,
    msg: string,
    sessionContextId?: string,
  ) {
    const token = await this.getToken();
    const baseUrl = await this.getBaseUrl();

    const processedMsg = this.prepareMessageWithCustomCall(msg);
    const { messageText, metadata } =
      await this.prepareMessageWithEmail(processedMsg);

    const res = await fetch(`${baseUrl}/chat/stream`, {
      method: 'POST',
      headers: this.buildHeaders(token),
      body: JSON.stringify({
        msg: messageText,
        metadata,
        newContext,
        contextId: sessionContextId || this.contextId,
      }),
    });

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();

    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split('\n\n');
      buffer = parts.pop()!;

      for (const part of parts) {
        if (part.startsWith('data: ')) {
          const json = part.replace('data: ', '');

          try {
            const event = JSON.parse(json);

            const contextId =
              event.contextId || event.context_id;

            if (contextId && event.kind === 'task') {
              this.contextId = contextId;
            }

            yield event;
          } catch (e) {
            console.warn('⚠️ JSON chunk incompleto, ignorando', json);
          }
        }
      }
    }
  }

  // =========================
  // ❌ CANCEL
  // =========================

  public async cancelTask(taskId: string): Promise<void> {
    const token = await this.getToken();
    const baseUrl = await this.getBaseUrl();

    await fetch(`${baseUrl}/chat/cancel`, {
      method: 'POST',
      headers: this.buildHeaders(token),
      body: JSON.stringify({ taskId }),
    });
  }

  // =========================
  // 🔗 EXTERNAL TOKENS
  // =========================

  public setExternalToken(
    provider: 'github' | 'jira' | 'confluence',
    token: string | null,
  ): void {
    if (token) this.externalTokens.set(provider, token);
    else this.externalTokens.delete(provider);
  }

  public isExternalProviderConnected(provider: string): boolean {
    return this.externalTokens.has(provider);
  }

  // =========================
  // 💬 FEEDBACK (SIN CAMBIO)
  // =========================

  public async submitFeedback(
    message: Message,
    feedback: Feedback,
  ): Promise<void> {
    if (!this.feedbackEndpoint) {
      throw new Error('Feedback endpoint is not configured');
    }

    const { token } = await this.identityApi.getCredentials();

    const { status } = await axios.post(
      this.feedbackEndpoint,
      {
        type: feedback.type,
        reason: feedback.reason,
        additionalFeedback: feedback.additionalFeedback || '',
        timestamp: createTimestamp(),
        message: message.text,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );

    if (status !== 200) {
      throw new Error('Failed to submit feedback');
    }
  }

  // =========================
  // 🔔 TOASTS (SIN CAMBIO)
  // =========================

  // private showToastWithCountdown(
  //   baseMessage: string,
  //   durationSeconds: number = 5,
  // ): void {
  //   const toast = document.createElement('div');
  //   Object.assign(toast.style, {
  //     position: 'fixed',
  //     top: '20px',
  //     left: '50%',
  //     transform: 'translateX(-50%)',
  //     backgroundColor: '#323232',
  //     color: '#fff',
  //     padding: '16px 24px',
  //     borderRadius: '8px',
  //     zIndex: '10000',
  //   });

  //   document.body.appendChild(toast);

  //   let secondsLeft = durationSeconds;

  //   const interval = setInterval(() => {
  //     secondsLeft--;
  //     toast.textContent = `${baseMessage} ${secondsLeft}s`;
  //     if (secondsLeft <= 0) clearInterval(interval);
  //   }, 1000);

  //   setTimeout(() => {
  //     clearInterval(interval);
  //     toast.remove();
  //   }, durationSeconds * 1000);
  // }
}